import React, { useState } from 'react';
import './GoogleSheetsTemplateSection.css';

const GoogleSheetsTemplateSection: React.FC = () => {
    const [copiedItem, setCopiedItem] = useState<string | null>(null);

    const copyToClipboard = (text: string, itemName: string) => {
        navigator.clipboard.writeText(text);
        setCopiedItem(itemName);
        setTimeout(() => setCopiedItem(null), 2000);
    };

    const formulas = {
        dateColumn: '=ARRAYFORMULA(IF(PeriodTemplate!A2:A="", "", $B$3+PeriodTemplate!E2:E))',
        dayColumn: '=ARRAYFORMULA(IF(PeriodTemplate!A2:A="", "", TEXT($B$3+PeriodTemplate!E2:E, "dddd")))',
        startTimeColumn: '=ARRAYFORMULA(PeriodTemplate!B2:B)',
        endTimeColumn: '=ARRAYFORMULA(PeriodTemplate!C2:C)',
        typeColumn: '=ARRAYFORMULA(PeriodTemplate!D2:D)',
        subjectColumn: '=ARRAYFORMULA(IF(H8:H="lesson", "", IF(H8:H="break", "Break", IF(H8:H="lunch", "Lunch", ""))))',
        weekStartDate: '=DATE(2026,1,6)+((B2-1)*7)',
    };

    const periodTemplateData = `Period\tStartTime\tEndTime\tType\tDayOffset
1\t08:00\t08:45\tlesson\t0
2\t08:45\t09:30\tlesson\t0
3\t09:30\t10:00\tbreak\t0
4\t10:00\t10:45\tlesson\t0
5\t10:45\t11:30\tlesson\t0
6\t11:30\t12:15\tlunch\t0
7\t12:15\t13:00\tlesson\t0
8\t13:00\t13:45\tlesson\t0`;

    const csvExample = `Date,Day,StartTime,EndTime,Subject,Teacher,Room,Type
2026-01-06,Monday,08:00,08:45,Mathematics,Ms. Jane,Room 101,lesson
2026-01-06,Monday,08:45,09:30,English,Mr. John,Room 102,lesson
2026-01-06,Monday,09:30,10:00,Break,,,break
2026-01-06,Monday,10:00,10:45,Science,Ms. Mary,Lab 1,lesson`;

    return (
        <div className="google-sheets-template">
            <div className="template-header">
                <h2>📊 Google Sheets Timetable Template</h2>
                <p className="template-description">
                    Use this template to auto-generate weekly timetables with formulas. Copy and paste into Google Sheets, fill in lessons, export as CSV, then upload here.
                </p>
            </div>

            {/* Step 1: Control Section */}
            <section className="template-section">
                <h3>Step 1: Set Up Control Section</h3>
                <p>Copy these cells into <strong>A1:B4</strong> of your Google Sheet:</p>

                <div className="control-table">
                    <table>
                        <thead>
                            <tr>
                                <th>Cell</th>
                                <th>Label</th>
                                <th>Formula/Value</th>
                                <th>Type</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                                <td>A1</td>
                                <td>Term</td>
                                <td>
                                    <code>Term 1 2026</code>
                                    <button
                                        className="copy-btn"
                                        onClick={() => copyToClipboard('Term 1 2026', 'term')}
                                    >
                                        {copiedItem === 'term' ? '✓ Copied!' : 'Copy'}
                                    </button>
                                </td>
                                <td>Dropdown</td>
                            </tr>
                            <tr>
                                <td>A2</td>
                                <td>Week Number</td>
                                <td>
                                    <code>1</code>
                                    <button
                                        className="copy-btn"
                                        onClick={() => copyToClipboard('1', 'weekNum')}
                                    >
                                        {copiedItem === 'weekNum' ? '✓ Copied!' : 'Copy'}
                                    </button>
                                </td>
                                <td>Dropdown (1-12)</td>
                            </tr>
                            <tr>
                                <td>B3</td>
                                <td>Week Start Date</td>
                                <td>
                                    <code className="formula">{formulas.weekStartDate}</code>
                                    <button
                                        className="copy-btn"
                                        onClick={() => copyToClipboard(formulas.weekStartDate, 'weekStart')}
                                    >
                                        {copiedItem === 'weekStart' ? '✓ Copied!' : 'Copy'}
                                    </button>
                                </td>
                                <td>Formula</td>
                            </tr>
                            <tr>
                                <td>A4</td>
                                <td>Target</td>
                                <td>
                                    <code>Year3blue</code>
                                    <button
                                        className="copy-btn"
                                        onClick={() => copyToClipboard('Year3blue', 'target')}
                                    >
                                        {copiedItem === 'target' ? '✓ Copied!' : 'Copy'}
                                    </button>
                                </td>
                                <td>Text (Year group or classroom)</td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </section>

            {/* Step 2: Period Template */}
            <section className="template-section">
                <h3>Step 2: Create "PeriodTemplate" Sheet</h3>
                <p>Create a new sheet called <strong>PeriodTemplate</strong> and paste this data:</p>

                <div className="code-block">
                    <pre>{periodTemplateData}</pre>
                    <button
                        className="copy-btn-large"
                        onClick={() => copyToClipboard(periodTemplateData, 'periodTemplate')}
                    >
                        {copiedItem === 'periodTemplate' ? '✓ Copied!' : 'Copy Period Template'}
                    </button>
                </div>

                <div className="info-box">
                    <strong>💡 Pro Tip:</strong> To add more days, duplicate rows 1-8 and change <code>DayOffset</code> to 1 (Tuesday), 2 (Wednesday), etc.
                </div>
            </section>

            {/* Step 3: Timetable Formulas */}
            <section className="template-section">
                <h3>Step 3: Add Auto-Generate Formulas</h3>
                <p>Go back to <strong>Sheet1</strong>. Add header row in <strong>Row 7</strong>:</p>
                <div className="header-row">
                    <code>Date | Day | StartTime | EndTime | Subject | Teacher | Room | Type</code>
                </div>

                <p>Then paste these formulas in <strong>Row 8</strong>:</p>

                <div className="formulas-grid">
                    <div className="formula-item">
                        <div className="formula-label">A8 (Date Column)</div>
                        <div className="formula-code">
                            <code>{formulas.dateColumn}</code>
                            <button
                                className="copy-btn"
                                onClick={() => copyToClipboard(formulas.dateColumn, 'dateCol')}
                            >
                                {copiedItem === 'dateCol' ? '✓' : 'Copy'}
                            </button>
                        </div>
                    </div>

                    <div className="formula-item">
                        <div className="formula-label">B8 (Day Column)</div>
                        <div className="formula-code">
                            <code>{formulas.dayColumn}</code>
                            <button
                                className="copy-btn"
                                onClick={() => copyToClipboard(formulas.dayColumn, 'dayCol')}
                            >
                                {copiedItem === 'dayCol' ? '✓' : 'Copy'}
                            </button>
                        </div>
                    </div>

                    <div className="formula-item">
                        <div className="formula-label">C8 (StartTime Column)</div>
                        <div className="formula-code">
                            <code>{formulas.startTimeColumn}</code>
                            <button
                                className="copy-btn"
                                onClick={() => copyToClipboard(formulas.startTimeColumn, 'startCol')}
                            >
                                {copiedItem === 'startCol' ? '✓' : 'Copy'}
                            </button>
                        </div>
                    </div>

                    <div className="formula-item">
                        <div className="formula-label">D8 (EndTime Column)</div>
                        <div className="formula-code">
                            <code>{formulas.endTimeColumn}</code>
                            <button
                                className="copy-btn"
                                onClick={() => copyToClipboard(formulas.endTimeColumn, 'endCol')}
                            >
                                {copiedItem === 'endCol' ? '✓' : 'Copy'}
                            </button>
                        </div>
                    </div>

                    <div className="formula-item">
                        <div className="formula-label">H8 (Type Column)</div>
                        <div className="formula-code">
                            <code>{formulas.typeColumn}</code>
                            <button
                                className="copy-btn"
                                onClick={() => copyToClipboard(formulas.typeColumn, 'typeCol')}
                            >
                                {copiedItem === 'typeCol' ? '✓' : 'Copy'}
                            </button>
                        </div>
                    </div>

                    <div className="formula-item">
                        <div className="formula-label">E8 (Subject Column - Auto-fills breaks)</div>
                        <div className="formula-code">
                            <code>{formulas.subjectColumn}</code>
                            <button
                                className="copy-btn"
                                onClick={() => copyToClipboard(formulas.subjectColumn, 'subCol')}
                            >
                                {copiedItem === 'subCol' ? '✓' : 'Copy'}
                            </button>
                        </div>
                    </div>
                </div>

                <div className="info-box">
                    <strong>⚠️ Important:</strong> Leave F8 and G8 (Teacher, Room) empty. These are for manual entry.
                </div>
            </section>

            {/* Step 4: Fill and Export */}
            <section className="template-section">
                <h3>Step 4: Fill Lessons & Export CSV</h3>
                <ol>
                    <li>Your timetable should now auto-populate with dates and times</li>
                    <li>Breaks and lunches are auto-filled</li>
                    <li>Fill in <strong>Subject, Teacher, Room</strong> for lesson rows only</li>
                    <li>Go to <strong>File → Download → CSV</strong></li>
                </ol>

                <p>Your CSV will look like this:</p>
                <div className="code-block">
                    <pre>{csvExample}</pre>
                    <button
                        className="copy-btn-large"
                        onClick={() => copyToClipboard(csvExample, 'csvEx')}
                    >
                        {copiedItem === 'csvEx' ? '✓ Copied!' : 'Copy Example'}
                    </button>
                </div>
            </section>

            {/* Step 5: Upload */}
            <section className="template-section highlight-section">
                <h3>Step 5: Upload to Pear</h3>
                <ol>
                    <li>Copy the entire CSV content from your exported file</li>
                    <li>Paste it into the upload section below</li>
                    <li>Select <strong>Type</strong>: Classroom or Year Group</li>
                    <li>Click <strong>Upload Timetable</strong></li>
                </ol>

                <div className="success-message">
                    ✅ Done! The timetable will appear in the Pear mobile app for parents.
                </div>
            </section>

            {/* Quick Reference */}
            <section className="template-section">
                <h3>📝 Quick Reference</h3>

                <div className="reference-grid">
                    <div className="reference-item">
                        <strong>Classroom Timetable</strong>
                        <p>Visible to all students in that specific classroom</p>
                    </div>
                    <div className="reference-item">
                        <strong>Year Group Timetable</strong>
                        <p>Visible to all students in that year group (e.g., Year3blue)</p>
                        <span className="priority-badge">⭐ Takes Priority</span>
                    </div>
                </div>

                <div className="info-box">
                    <strong>Priority Logic:</strong> If both a Classroom and Year Group timetable exist for the same week,
                    students will see the <strong>Year Group</strong> timetable. This allows you to set a general timetable
                    for all Year 3 students while still having classroom-specific variations if needed.
                </div>
            </section>
        </div>
    );
};

export default GoogleSheetsTemplateSection;
